import { COMPLIANCE_TRAINING_MODULE_CONTENT } from 'Core-utils/constants/constants';
import { ComplianceTrainingCustomModulesSchema } from '@ampsec/platform-client';

/**
 * Contentful securityHubI18n entry key. The `value` field is a JSON array of module objects
 * (see ComplianceTrainingModuleFromI18n).
 */
export { COMPLIANCE_TRAINING_MODULE_CONTENT as COMPLIANCE_TRAINING_MODULE_CONTENT_I18N_KEY };

export type ComplianceTrainingModuleFromI18n = {
    subject: string;
    name: string;
    description: string;
    videoUrl: string;
    thumbnail?: string;
};

export type ComplianceTrainingSubject = ComplianceTrainingModuleFromI18n & {
    enabled: boolean;
};

const isModuleEntry = (value: unknown): value is ComplianceTrainingModuleFromI18n => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const entry = value as Record<string, unknown>;
    return (
        typeof entry.subject === 'string' &&
        typeof entry.name === 'string' &&
        typeof entry.description === 'string' &&
        typeof entry.videoUrl === 'string' &&
        (entry.thumbnail === undefined || typeof entry.thumbnail === 'string')
    );
};

const toComplianceTrainingSubjects = (modules: ComplianceTrainingModuleFromI18n[]): ComplianceTrainingSubject[] =>
    modules.map((entry) => ({
        ...entry,
        enabled: false,
    }));

/** Parses Contentful i18n JSON array; invalid input yields []. */
export const parseComplianceTrainingModulesFromI18n = (raw: string | null | undefined): ComplianceTrainingSubject[] => {
    const trimmed = raw?.trim();
    if (!trimmed) {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return toComplianceTrainingSubjects(parsed.filter(isModuleEntry));
    } catch {
        return [];
    }
};

/** Parses compliance.training.custom.modules amp setting value; invalid input yields []. */
export const parseComplianceTrainingCustomModules = (raw: unknown): ComplianceTrainingSubject[] => {
    let parsed = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) {
            return [];
        }
        try {
            parsed = JSON.parse(trimmed);
        } catch {
            return [];
        }
    }

    const validated = ComplianceTrainingCustomModulesSchema.safeParse(parsed);
    if (!validated.success) {
        return [];
    }

    return toComplianceTrainingSubjects(validated.data);
};

const serializeAmpSettingValue = (raw: unknown): string => {
    if (raw === undefined || raw === null) {
        return '';
    }
    return typeof raw === 'string' ? raw : JSON.stringify(raw);
};

/** Stable string signature for amp setting values used in React dependency arrays. */
export const getComplianceTrainingCustomModulesSettingSignature = (
    tenantSettings: Record<string, { value?: unknown } | undefined> | undefined,
    settingKey: string | undefined,
): string => {
    if (!settingKey) {
        return '';
    }
    return serializeAmpSettingValue(tenantSettings?.[settingKey]?.value);
};

/** Parses custom modules from a stable setting signature. */
export const parseComplianceTrainingCustomModulesFromSettingSignature = (
    signature: string,
): ComplianceTrainingSubject[] => {
    if (!signature) {
        return [];
    }

    try {
        const raw = signature.startsWith('[') || signature.startsWith('{') ? JSON.parse(signature) : signature;
        return parseComplianceTrainingCustomModules(raw);
    } catch {
        return [];
    }
};

/** Appends tenant custom modules after Contentful modules, skipping duplicate subjects. */
export const mergeComplianceTrainingModules = (
    contentfulModules: ComplianceTrainingSubject[],
    customModules: ComplianceTrainingSubject[],
): ComplianceTrainingSubject[] => {
    const seenSubjects = new Set(contentfulModules.map((module) => module.subject));
    const uniqueCustomModules = customModules.filter((module) => !seenSubjects.has(module.subject));
    return [...contentfulModules, ...uniqueCustomModules];
};
